import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { formatCurrency } from '../../../utils/formatters';
import { explainAccountFunding } from '../../../utils/requirementCalculations';

const FundingLine = ({ label, value, strong = false }) => (
  <div className={`requirements-funding-line${strong ? ' requirements-funding-line-total' : ''}`}>
    <span className="requirements-funding-line-label">{label}</span>
    <span className="requirements-funding-line-value">
      {formatCurrency(value)}
      <span className="text-muted"> / fn</span>
    </span>
  </div>
);

FundingLine.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  strong: PropTypes.bool,
};

const AccountFundingBreakdown = ({
  requirements,
  fortnightStart,
  fortnightEnd,
  defaultCollapsed = true,
}) => {
  const [isOpen, setIsOpen] = useState(!defaultCollapsed);
  const funding = explainAccountFunding(requirements, fortnightStart, fortnightEnd);
  const panelId = 'funding-breakdown-panel';

  return (
    <div className="requirements-funding-breakdown-wrap">
      <button
        type="button"
        className="requirements-funding-breakdown-toggle"
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
        aria-controls={panelId}
      >
        <span className="requirements-funding-breakdown-title">Funding breakdown</span>
        <span className="text-muted small me-2">{formatCurrency(funding.total)} / fn</span>
        <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} className="requirements-funding-chevron" />
      </button>
      {isOpen && (
        <div className="requirements-funding-breakdown" id={panelId} aria-label="Funding breakdown">
          {funding.hasBase ? (
            <>
              <FundingLine label="Minimum allocation (target)" value={funding.baseTarget} />
              <FundingLine label="Other allocations (sum)" value={funding.othersSum} />
              <FundingLine label="Core required (max of above)" value={funding.core} />
            </>
          ) : (
            <FundingLine label="Allocations & bills (sum)" value={funding.othersSum} />
          )}
          {funding.additiveSum > 0 && (
            <FundingLine label="Additive extras" value={funding.additiveSum} />
          )}
          <FundingLine label="Total required" value={funding.total} strong />
          {funding.excludedCount > 0 && (
            <p className="text-muted small mb-0 mt-2">
              {funding.excludedCount} bill line{funding.excludedCount === 1 ? '' : 's'} excluded from
              transfer target (still on Bills page).
            </p>
          )}
        </div>
      )}
    </div>
  );
};

AccountFundingBreakdown.propTypes = {
  requirements: PropTypes.arrayOf(PropTypes.object).isRequired,
  fortnightStart: PropTypes.instanceOf(Date).isRequired,
  fortnightEnd: PropTypes.instanceOf(Date).isRequired,
  defaultCollapsed: PropTypes.bool,
};

export default AccountFundingBreakdown;
